import React, { useMemo } from "react";
import { Alert, Box, Typography } from "@mui/material";
import { ItGlueAccessFeedback, itGlueAccessStatusLabel } from "../utils/itGlueAccessChecks";

type ItGlueAccessFeedbackAlertProps = {
  feedback: ItGlueAccessFeedback;
  connectionMessage?: string;
  onClose?: () => void;
};

/** Single body size for the whole alert (weight/color carry hierarchy). */
const TEXT = {
  fontFamily: "var(--theo-font-sans)",
  fontSize: 13,
  lineHeight: 1.4,
} as const;

/**
 * IT Glue connection test breakdown (API key + documentation access). Layout: title, optional missing line, checklist.
 * Design: compact spacing; avoid duplicating the connection chip line when the alert already states outcome.
 */
const ItGlueAccessFeedbackAlert: React.FC<ItGlueAccessFeedbackAlertProps> = ({
  feedback,
  connectionMessage,
  onClose,
}) => {
  const failedLabels = useMemo(
    () => new Set(feedback.permissionChecks.filter((c) => !c.ok).map((c) => c.permission)),
    [feedback.permissionChecks]
  );

  const missingFiltered = useMemo(
    () => feedback.missingRequirements.filter((m) => !failedLabels.has(m)),
    [feedback.missingRequirements, failedLabels]
  );

  const showConnectionBanner = Boolean(
    connectionMessage &&
      !(feedback.alertSeverity === "warning" && /successful/i.test(connectionMessage))
  );

  return (
    <Alert
      severity={feedback.alertSeverity}
      onClose={onClose}
      sx={{
        mt: 2,
        py: 1.25,
        "& .MuiAlert-message": { width: "100%" },
      }}
    >
      {showConnectionBanner ? (
        <Typography sx={{ ...TEXT, mb: 0.75 }}>{connectionMessage}</Typography>
      ) : null}
      <Typography
        sx={{
          ...TEXT,
          fontWeight: 600,
          mb: missingFiltered.length ? 0.5 : 0.75,
        }}
      >
        {feedback.message}
      </Typography>
      {missingFiltered.length > 0 ? (
        <Typography sx={{ ...TEXT, color: "text.secondary", mb: 0.75 }}>
          Missing: {missingFiltered.join(", ")}
        </Typography>
      ) : null}
      {feedback.permissionChecks.length > 0 ? (
        <Box
          component="ul"
          sx={{
            m: 0,
            mb: 0,
            pl: 2,
            listStyleType: "disc",
            "& li": { mb: 0.5 },
            "& li:last-of-type": { mb: 0 },
          }}
        >
          {feedback.permissionChecks.map((check, idx) => (
            <Typography
              key={`${check.permission}-${idx}`}
              component="li"
              sx={{ ...TEXT, whiteSpace: "pre-line" }}
            >
              <strong>{itGlueAccessStatusLabel(check)}</strong> {check.permission}
              {check.reason ? (
                <Typography component="span" sx={{ ...TEXT, display: "block", mt: 0.25, fontWeight: 400 }}>
                  {check.reason}
                </Typography>
              ) : null}
            </Typography>
          ))}
        </Box>
      ) : null}
      {feedback.allChecksPassed ? (
        <Typography sx={{ ...TEXT, mt: 0.75 }}>All required checks passed.</Typography>
      ) : feedback.alertSeverity === "warning" ? (
        <Typography sx={{ ...TEXT, color: "text.secondary", mt: 0.75 }}>
          You can save; update IT Glue when you want full article search.
        </Typography>
      ) : null}
    </Alert>
  );
};

export default ItGlueAccessFeedbackAlert;
